import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, useIsDesktop } from "../reportTokens";
import { ReportSectionExportButton } from "./ReportExportButton";
import { getDiscountSourceBreakdown, DISCOUNT_SOURCES, type DiscountSourceKey } from "../loyaltyRedemption";
import { getDiscountLog, getDiscountStaff, type DiscountLogRow } from "../discountLog";
import { useDiscountPerformance, fetchDiscountPerformance, type DiscountPerformanceSegment, type DiscountPerformanceSource } from "../../../hooks/useDiscountPerformance";
import { useDiscountLog, fetchDiscountLog, type DiscountLogItem } from "../../../hooks/useDiscountLog";
import { formatMoneyForExport } from "../../../utils/currency";
import SectionError from "../SectionError";
import SectionEmpty from "../SectionEmpty";
import { Bone } from "../reportTokens";

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED DISCOUNTS DRILL-DOWN  (SNAP-DSC-1)
   One component used by BOTH the Snapshot and Sales reports so the KPI set,
   source breakdown, Discounts-by-Staff list, and order-level Discount Log are
   identical everywhere and never diverge.

   A single shared source-selection (multi-select Set) drives the breakdown
   tabs, the Discounts-by-Staff list, and the Discount Log's Discount-column
   filter — two entry points, one state, always in sync.
   Frontend sample data; Loyalty subtotal ties to the Loyalty Report.
   ═══════════════════════════════════════════════════════════════════════════ */

const fmtMoney = (n: number, sym = "$"): string =>
    `${sym}${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n: number): string => Math.round(n || 0).toLocaleString();
const fmtTime = (t: string): string => {
    const [h, m] = (t || "").split(":").map(Number);
    if (Number.isNaN(h)) return t || "—";
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};
const srcColor = (k: string): string => (k === "loyalty" ? T.purple : k === "promotional" ? T.blue : k === "thirdParty" ? T.teal : T.orange);
const badgeText = (r: DiscountLogRow): string =>
    r.source === "loyalty" ? `Loyalty · ${r.name}` : r.source === "promotional" ? `Promotional · ${r.name}` : r.source === "thirdParty" ? "3rd Party" : r.name;
const ALL_KEYS = DISCOUNT_SOURCES.map((s) => s.key);
const labelFor = (k: DiscountSourceKey) => DISCOUNT_SOURCES.find((s) => s.key === k)!.label;
/** discount-performance `source` values → the drill-down's source keys. The
 *  backend now returns its own THIRD_PARTY segment alongside PROMOTIONAL /
 *  LOYALTY / MANUAL, so every source maps 1:1 — no client-side splitting. */
const API_SOURCE_TO_KEY: Record<string, DiscountSourceKey> = {
    PROMOTIONAL: "promotional",
    LOYALTY: "loyalty",
    MANUAL: "manualComp",
    THIRD_PARTY: "thirdParty",
};
const KEY_TO_API_SOURCE: Record<DiscountSourceKey, DiscountPerformanceSource> = {
    promotional: "PROMOTIONAL",
    loyalty: "LOYALTY",
    manualComp: "MANUAL",
    thirdParty: "THIRD_PARTY",
};
/** Fold discount-performance segments into the drill-down's source keys.
 *  Shared by the card and its Export so both read the response identically. */
const amountsFromSegments = (segments?: DiscountPerformanceSegment[]): Record<DiscountSourceKey, number> => {
    const amounts: Record<DiscountSourceKey, number> = { promotional: 0, loyalty: 0, manualComp: 0, thirdParty: 0 };
    for (const seg of segments ?? []) {
        const key = API_SOURCE_TO_KEY[String(seg.source).toUpperCase()];
        if (key) amounts[key] += seg.discountAmount;
    }
    return amounts;
};

export interface DiscountsDrilldownProps {
    sym: string;
    startISO: string;
    endISO: string;
    isToday?: boolean;
    outletId?: string | null;
    /** Gross sales for "% of Gross" (real, identical in both reports). */
    grossTotal?: number;
    /** All-orders count for "% of all". */
    totalOrders?: number;
    /** Opens the report's OrderDetailSlider for a discounted order. */
    onOpenOrder?: (row: { orderId: string; orderNo: string }) => void;
}

const STAFF_PAGE = 5;
const LOG_PAGE = 8;
/** Only the Date column is sortable, and the endpoint sorts by response-field
 *  name (the same convention as its `sortBy=amount` example). */
const LOG_SORT_BY = "orderDate";

function MiniK({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
    return (
        <div className="rpt-card rpt-card--r12" style={{ flex: 1, minWidth: 120, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: color || T.t1, marginTop: 5, whiteSpace: "nowrap" }}>{value}</div>
            {sub && <div style={{ fontSize: 10, color: T.t3, marginTop: 3 }}>{sub}</div>}
        </div>
    );
}

const pagerBtn = (disabled: boolean): React.CSSProperties => ({
    padding: "4px 10px", borderRadius: 6, border: `1px solid ${disabled ? T.divider : T.accent}`,
    background: "none", color: disabled ? T.t3 : T.accent, fontSize: 11, fontWeight: 600,
    cursor: disabled ? "default" : "pointer", fontFamily: T.font,
});

/* ─── Discount-column source filter (multi-select). Popover on desktop, bottom
      sheet on mobile. Binds to the same `selected` set as the breakdown tabs. ──
   The desktop popover is portaled to <body> and positioned `fixed` off the
   trigger button's own rect (not `absolute` inside the card). The card wraps
   its table in `overflow: hidden`; an `absolute` popover shares that box as
   its containing block and gets clipped by it whenever the card is short (few
   rows) — `fixed` positioning via a portal escapes that ancestor entirely. */
function SourceFilterControl({ selected, onApply, isDesktop }: {
    selected: Set<DiscountSourceKey>; onApply: (next: Set<DiscountSourceKey>) => void; isDesktop: boolean;
}) {
    const [open, setOpen] = useState(false);
    // Edits stay local until "Apply" — the API call (driven by the parent's committed
    // `selected`) only fires once, on Apply, instead of once per checkbox click.
    const [draft, setDraft] = useState<Set<DiscountSourceKey>>(selected);
    const ref = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState({ top: 0, right: 0 });

    // Re-seed the draft from the last applied selection every time the popover/sheet
    // opens, so closing without Apply discards whatever was checked in the meantime.
    useEffect(() => { if (open) setDraft(new Set(selected)); }, [open, selected]);

    /* Flips above the button when there isn't enough room below it (e.g. the
       Discount Log's last rows, near the bottom of the viewport) — measured
       off the menu's own rendered height, not a guessed constant, since it
       varies with DISCOUNT_SOURCES.length. Clamped into the viewport as a
       last resort so it's never partly offscreen either way. */
    const updatePosition = useCallback(() => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const gap = 6;
        const menuH = menuRef.current?.getBoundingClientRect().height ?? 0;
        const spaceBelow = window.innerHeight - r.bottom;
        const flipUp = menuH > 0 && spaceBelow < menuH + gap && r.top - gap > menuH;
        let top = flipUp ? r.top - menuH - gap : r.bottom + gap;
        top = Math.max(8, Math.min(top, window.innerHeight - 8 - menuH));
        setCoords({ top, right: window.innerWidth - r.right });
    }, []);

    useLayoutEffect(() => {
        if (!open || !isDesktop) return;
        updatePosition();
    }, [open, isDesktop, updatePosition]);

    useEffect(() => {
        if (!open || !isDesktop) return;
        const onScrollOrResize = () => updatePosition();
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
        };
    }, [open, isDesktop, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const h = (e: MouseEvent) => {
            const t = e.target as Node;
            if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    const active = selected.size !== DISCOUNT_SOURCES.length;

    const toggleDraft = (k: DiscountSourceKey) => setDraft((prev) => {
        const n = new Set(prev);
        if (n.has(k)) { if (n.size > 1) n.delete(k); } else n.add(k);
        return n;
    });
    const setDraftAll = () => setDraft(new Set(ALL_KEYS));
    const apply = () => { onApply(draft); setOpen(false); };

    const CheckRow = ({ k, big }: { k: DiscountSourceKey; big?: boolean }) => {
        const on = draft.has(k);
        return (
            <div onClick={() => toggleDraft(k)} style={{ display: "flex", alignItems: "center", gap: 10, padding: big ? "12px 16px" : "9px 12px", fontSize: big ? 14 : 12, cursor: "pointer", color: T.t1 }}>
                <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on ? srcColor(k) : T.cardBorder}`, background: on ? srcColor(k) : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {on && <span className="material-symbols-rounded" style={{ fontSize: 13, color: "#fff" }}>check</span>}
                </span>
                {labelFor(k)}
            </div>
        );
    };

    const ApplyBtn = ({ big }: { big?: boolean }) => (
        <button onClick={apply} style={{ width: "100%", height: big ? 42 : 32, border: "none", borderRadius: 8, fontSize: big ? 14 : 12, fontWeight: 600, color: "#fff", background: T.accent, cursor: "pointer", fontFamily: T.font }}>Apply</button>
    );

    return (
        <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
            <button ref={btnRef} type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} title="Filter by source"
                style={{ display: "inline-flex", alignItems: "center", gap: 3, border: "none", background: "none", cursor: "pointer", padding: 0, color: active ? T.accent : T.t3 }}>
                <span className="material-symbols-rounded" style={{ fontSize: 15 }}>filter_list</span>
                {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.accent }} />}
            </button>
            {open && isDesktop && typeof document !== "undefined" && createPortal(
                <div ref={menuRef} onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: coords.top, right: coords.right, width: 190, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", zIndex: 1200, overflow: "hidden", textTransform: "none", letterSpacing: 0 }}>
                    {DISCOUNT_SOURCES.map((s) => <CheckRow key={s.key} k={s.key} />)}
                    <div onClick={setDraftAll} style={{ padding: "8px 12px", fontSize: 11, cursor: "pointer", color: T.accent, fontWeight: 600, borderTop: `1px solid ${T.divider}` }}>Select all</div>
                    <div style={{ padding: 8, borderTop: `1px solid ${T.divider}` }}><ApplyBtn /></div>
                </div>,
                document.body
            )}
            {open && !isDesktop && (
                /* 1100, not 1000: BottomNav is fixed at z-index 1000 and paints
                   later in the tree, so an equal value leaves the sheet's lower
                   rows and its Apply button behind the nav bar. */
                <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", flexDirection: "column", justifyContent: "flex-end" }} onClick={() => setOpen(false)}>
                    <div style={{ flex: 1, background: "rgba(0,0,0,0.3)" }} />
                    <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: "16px 16px 0 0", padding: "8px 0 max(16px, env(safe-area-inset-bottom))", textTransform: "none", letterSpacing: 0 }}>
                        <div style={{ display: "flex", justifyContent: "center", padding: "6px 0" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: T.cardBorder }} /></div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 16px 10px" }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>Filter by source</span>
                            <span onClick={setDraftAll} style={{ fontSize: 12, fontWeight: 600, color: T.accent, cursor: "pointer" }}>Select all</span>
                        </div>
                        {DISCOUNT_SOURCES.map((s) => <CheckRow key={s.key} k={s.key} big />)}
                        <div style={{ padding: "12px 16px 0" }}><ApplyBtn big /></div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function DiscountsDrilldown({
    sym, startISO, endISO, isToday, outletId = null, grossTotal = 0, totalOrders = 0, onOpenOrder,
}: DiscountsDrilldownProps) {
    const isDesktop = useIsDesktop();
    const [selected, setSelected] = useState<Set<DiscountSourceKey>>(() => new Set(ALL_KEYS));
    const [staffPage, setStaffPage] = useState(0);
    const [logPage, setLogPage] = useState(0);
    const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");

    const log = useMemo(() => getDiscountLog(startISO, endISO, outletId), [startISO, endISO, outletId]);
    const breakdown = useMemo(() => getDiscountSourceBreakdown(startISO, endISO, outletId), [startISO, endISO, outletId]);

    const scopedLog = useMemo(() => {
        const rows = log.filter((r) => selected.has(r.source));
        return rows.sort((a, z) => (a.ts < z.ts ? (sortDir === "desc" ? 1 : -1) : a.ts > z.ts ? (sortDir === "desc" ? -1 : 1) : 0));
    }, [log, selected, sortDir]);
    const staff = useMemo(() => getDiscountStaff(scopedLog), [scopedLog]);

    useEffect(() => { setStaffPage(0); setLogPage(0); }, [selected, startISO, endISO, outletId, sortDir]);

    // Shared filter mutators — used by the breakdown tabs. The column filter (SourceFilterControl)
    // owns its own draft state and only reaches setSelected once, via onApply.
    const setAll = () => setSelected(new Set(ALL_KEYS));
    const setOnly = (k: DiscountSourceKey) => setSelected(new Set([k]));

    const total = breakdown.total;
    const isAll = selected.size === DISCOUNT_SOURCES.length;
    const scopedTotal = ALL_KEYS.filter((k) => selected.has(k)).reduce((a, k) => a + breakdown[k], 0);
    const distinctOrders = useMemo(() => new Set(log.map((r) => r.orderNo)).size, [log]);
    const visibleSources = DISCOUNT_SOURCES.filter((s) => selected.has(s.key));
    const selectionLabel = isAll ? "" : ALL_KEYS.filter((k) => selected.has(k)).map(labelFor).join(", ");

    if (total <= 0) {
        return <div style={{ padding: 40, textAlign: "center", color: T.t3, fontSize: 13 }}>No discounts for the selected period.</div>;
    }

    /* ── KPI row (unaffected by the source filter) ── */
    const kpiRow = (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <MiniK label="Total Discounts" value={fmtMoney(total, sym)} color={T.orange} sub={grossTotal > 0 ? `${((total / grossTotal) * 100).toFixed(1)}% of Gross` : undefined} />
            <MiniK label="Disc. Orders" value={fmtNum(distinctOrders)} sub={totalOrders > 0 ? `${((distinctOrders / totalOrders) * 100).toFixed(1)}% of all` : undefined} />
        </div>
    );

    /* ── Source breakdown card (tabs share the filter state) ── */
    const pills = (["all", ...ALL_KEYS] as ("all" | DiscountSourceKey)[]);
    const breakdownCard = (
        <div className="rpt-card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discount &amp; Promotion Performance</div>
                    <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>By source · Loyalty Redemption reconciles with the Loyalty Report</div>
                </div>
                <ReportSectionExportButton
                    variant={isDesktop ? "desktop" : "mobile"}
                    title="Discount by Source"
                    filename="discount-by-source"
                    buildExport={() => ({
                        headers: ["Source", "Discount", "Share"],
                        rows: DISCOUNT_SOURCES.map((s) => [s.label, `${sym}${(breakdown[s.key]).toFixed(2)}`, total > 0 ? `${((breakdown[s.key] / total) * 100).toFixed(1)}%` : "—"]),
                    })}
                />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {pills.map((k) => {
                    const activeTab = k === "all" ? isAll : (selected.size === 1 && selected.has(k as DiscountSourceKey));
                    const label = k === "all" ? (isDesktop ? "All Sources" : "All") : (isDesktop ? labelFor(k as DiscountSourceKey) : DISCOUNT_SOURCES.find((s) => s.key === k)!.mobileLabel);
                    return (
                        <button key={k} type="button" onClick={() => (k === "all" ? setAll() : setOnly(k as DiscountSourceKey))}
                            style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${activeTab ? T.accent : T.cardBorder}`, background: activeTab ? T.accentSoft : T.surface, color: activeTab ? T.accent : T.t2, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
                            {label}
                        </button>
                    );
                })}
            </div>
            {visibleSources.map((s, i) => {
                const val = breakdown[s.key];
                const share = total > 0 ? (val / total) * 100 : 0;
                const color = srcColor(s.key);
                return (
                    <div key={s.key} style={{ padding: "10px 0", borderBottom: i < visibleSources.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5, gap: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>
                                {s.label}
                                {s.key === "loyalty" && <span style={{ fontSize: 10, color: T.t3, fontWeight: 400, marginLeft: 6 }}>liability release</span>}
                            </span>
                            <span style={{ fontSize: 12, color: T.t2, whiteSpace: "nowrap" }}><strong style={{ color }}>{fmtMoney(val, sym)}</strong> · {share.toFixed(1)}%</span>
                        </div>
                        <div style={{ height: 8, background: T.hover, borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, share))}%`, background: `${color}cc`, borderRadius: 4 }} />
                        </div>
                    </div>
                );
            })}
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11, color: T.t3 }}>
                <span>{isAll ? "Total discounts" : "Scoped total"}: <span style={{ color: T.orange, fontWeight: 700 }}>{fmtMoney(scopedTotal, sym)}</span></span>
            </div>
        </div>
    );

    /* ── Discounts by Staff/Platform (scoped to the selection) ── */
    const staffTotalPages = Math.max(1, Math.ceil(staff.length / STAFF_PAGE));
    const staffSafePage = Math.min(staffPage, staffTotalPages - 1);
    const staffSlice = staff.slice(staffSafePage * STAFF_PAGE, staffSafePage * STAFF_PAGE + STAFF_PAGE);
    const staffTotal = staff.reduce((a, r) => a + r.amount, 0);
    const staffCard = (
        <div className="rpt-card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discounts by Staff/ Platform</div>
            <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>Who applied the discounts{isAll ? "" : ` · ${selectionLabel}`}</div>
            {staff.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: T.t3, fontSize: 12 }}>No staff discounts for this selection.</div>
            ) : (
                <>
                    {staffSlice.map((s, i) => {
                        const rank = staffSafePage * STAFF_PAGE + i;
                        const pct = staffTotal > 0 ? ((s.amount / staffTotal) * 100).toFixed(1) : "0.0";
                        return (
                            <div key={s.staff} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: i < staffSlice.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                                <div style={{ width: 22, height: 22, borderRadius: 6, background: rank === 0 ? T.orangeSoft : T.hover, color: rank === 0 ? T.orange : T.t3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{rank + 1}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: s.staff === "System" ? T.t2 : T.accent, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.staff}</div>
                                    <div style={{ fontSize: 11, color: T.t3, marginTop: 1 }}>{s.orders} orders · {pct}%</div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.orange, flexShrink: 0 }}>{fmtMoney(s.amount, sym)}</div>
                            </div>
                        );
                    })}
                    {staffTotalPages > 1 && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, paddingTop: 4 }}>
                            <button onClick={() => setStaffPage((p) => Math.max(0, p - 1))} disabled={staffSafePage === 0} style={pagerBtn(staffSafePage === 0)}>← Prev</button>
                            <span style={{ fontSize: 10, color: T.t3 }}>{staffSafePage + 1} / {staffTotalPages}</span>
                            <button onClick={() => setStaffPage((p) => Math.min(staffTotalPages - 1, p + 1))} disabled={staffSafePage >= staffTotalPages - 1} style={pagerBtn(staffSafePage >= staffTotalPages - 1)}>Next →</button>
                        </div>
                    )}
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between", fontSize: 11, color: T.t3 }}>
                        <span>{staff.length} staff</span>
                        <span>Total: <span style={{ color: T.orange, fontWeight: 600 }}>{fmtMoney(staffTotal, sym)}</span></span>
                    </div>
                </>
            )}
        </div>
    );

    /* ── Discount Log ── */
    const logTotalPages = Math.max(1, Math.ceil(scopedLog.length / LOG_PAGE));
    const logSafePage = Math.min(logPage, logTotalPages - 1);
    const logSlice = scopedLog.slice(logSafePage * LOG_PAGE, logSafePage * LOG_PAGE + LOG_PAGE);
    const tpl = "0.9fr 1.1fr 0.9fr 1.2fr 1.9fr 0.9fr";
    const cellClip = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 } as const;
    const emptyMsg = selected.size === 0
        ? "No discounts for the selected period."
        : isAll ? "No discounts for the selected period." : `No ${selectionLabel} discounts for the selected period.`;
    const Badge = ({ r }: { r: DiscountLogRow }) => (
        <span style={{ display: "inline-block", fontSize: 10, fontWeight: 600, color: srcColor(r.source), background: `${srcColor(r.source)}18`, padding: "2px 8px", borderRadius: 20, maxWidth: "100%", ...cellClip }}>{badgeText(r)}</span>
    );
    const orderCell = (r: DiscountLogRow) => (
        <span onClick={onOpenOrder ? () => onOpenOrder({ orderId: r.orderId, orderNo: r.orderNo }) : undefined}
            style={{ fontSize: 11.5, fontWeight: 600, color: onOpenOrder ? T.accent : T.t2, cursor: onOpenOrder ? "pointer" : "default", ...cellClip }} title={r.orderNo}>#{r.orderNo}</span>
    );

    const logCard = (
        <div className="rpt-card" style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discount Log</div>
                    <div style={{ fontSize: 11, color: T.t3 }}>Discounted order details{isAll ? "" : ` · ${selectionLabel}`}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!isDesktop && <SourceFilterControl selected={selected} onApply={setSelected} isDesktop={false} />}
                    <ReportSectionExportButton
                        variant={isDesktop ? "desktop" : "mobile"}
                        title="Discount Log"
                        filename="discount-log"
                        buildExport={() => ({
                            headers: ["Order #", "Date", "Time", "Staff", "Discount", "Amount"],
                            rows: scopedLog.map((r) => [`#${r.orderNo}`, r.dateISO, fmtTime(r.time), r.staff, badgeText(r), `${sym}${r.amount.toFixed(2)}`]),
                        })}
                    />
                </div>
            </div>
            {scopedLog.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: T.t3, fontSize: 12 }}>{emptyMsg}</div>
            ) : isDesktop ? (
                <div style={{ marginTop: 8 }}>
                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: 640 }}>
                            <div style={{ display: "grid", gridTemplateColumns: tpl, gap: 8, padding: "6px 0", borderBottom: `1px solid ${T.divider}`, marginBottom: 4 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Order #</div>
                                <div onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, cursor: "pointer", userSelect: "none" }}>
                                    Date <span style={{ marginLeft: 2 }}>{sortDir === "desc" ? "▾" : "▴"}</span>
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Time</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Staff</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 4 }}>
                                    Discount <SourceFilterControl selected={selected} onApply={setSelected} isDesktop />
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "right" }}>Amount</div>
                            </div>
                            {logSlice.map((r, i) => (
                                <div key={r.id} style={{ display: "grid", gridTemplateColumns: tpl, gap: 8, padding: "8px 0", alignItems: "center", borderBottom: i < logSlice.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                                    <div style={cellClip}>{orderCell(r)}</div>
                                    <div style={{ fontSize: 11.5, color: T.t2, fontFamily: T.font, whiteSpace: "nowrap" }}>{r.dateISO}</div>
                                    <div style={{ fontSize: 11, color: T.t3, whiteSpace: "nowrap" }}>{fmtTime(r.time)}</div>
                                    <div title={r.staff} style={{ fontSize: 11.5, fontWeight: 600, color: r.staff === "System" ? T.t2 : T.accent, ...cellClip }}>{r.staff}</div>
                                    <div style={cellClip}><Badge r={r} /></div>
                                    <div style={{ fontSize: 11.5, fontWeight: 600, color: T.orange, textAlign: "right", whiteSpace: "nowrap" }}>{fmtMoney(r.amount, sym)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {logTotalPages > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>{logSafePage * LOG_PAGE + 1}–{Math.min((logSafePage + 1) * LOG_PAGE, scopedLog.length)} of {scopedLog.length}</span>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <button onClick={() => setLogPage((p) => Math.max(0, p - 1))} disabled={logSafePage === 0} style={pagerBtn(logSafePage === 0)}>← Prev</button>
                                <span style={{ fontSize: 11, color: T.t2, padding: "4px 6px" }}>{logSafePage + 1} / {logTotalPages}</span>
                                <button onClick={() => setLogPage((p) => Math.min(logTotalPages - 1, p + 1))} disabled={logSafePage >= logTotalPages - 1} style={pagerBtn(logSafePage >= logTotalPages - 1)}>Next →</button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                    {logSlice.map((r) => (
                        <div key={r.id} style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
                                {orderCell(r)}
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.orange }}>{fmtMoney(r.amount, sym)}</span>
                            </div>
                            <div style={{ marginBottom: 6 }}><Badge r={r} /></div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.t3 }}>
                                <span>{r.staff}</span>
                                <span>{r.dateISO} · {fmtTime(r.time)}</span>
                            </div>
                        </div>
                    ))}
                    {logTotalPages > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                            <button onClick={() => setLogPage((p) => Math.max(0, p - 1))} disabled={logSafePage === 0} style={pagerBtn(logSafePage === 0)}>← Prev</button>
                            <span style={{ fontSize: 10, color: T.t3 }}>{logSafePage + 1} / {logTotalPages}</span>
                            <button onClick={() => setLogPage((p) => Math.min(logTotalPages - 1, p + 1))} disabled={logSafePage >= logTotalPages - 1} style={pagerBtn(logSafePage >= logTotalPages - 1)}>Next →</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div>
            {kpiRow}
            {breakdownCard}
            {staffCard}
            {logCard}
        </div>
    );
}

export interface DiscountBreakdownAndLogProps {
    sym: string;
    startISO: string;
    endISO: string;
    outletId?: string | null;
    /** Outlet UUID for the live discount-performance API. Without it the
     *  breakdown card renders its empty state (no call is made). */
    locationId?: string | null;
    /** Today filter — startDate/endDate are omitted from the API call so the
     *  backend serves the live window. Historical ranges send both. */
    isToday?: boolean;
    /** Global sales-channel filter → `orderSource` query param. Omit for Overall. */
    orderSource?: "direct" | "thirdparty";
    /** Bearer token; falls back to the stored credentials when omitted. */
    token?: string | null;
    /** Opens the report's OrderDetailSlider for a discounted order. */
    onOpenOrder?: (row: { orderId: string; orderNo: string }) => void;
    /** Rendered between the breakdown card and the log card — e.g. a
     *  real-API-driven "Discounts by Staff" card, to keep the on-screen
     *  order Breakdown → Staff → Log without coupling their state. */
    children?: React.ReactNode;
}

/**
 * Discount & Promotion Performance (by-source breakdown) + Discount Log —
 * for use alongside a real-API-driven KPI row / Discounts-by-Staff list
 * (passed as `children`, rendered between the two).
 *
 * Both cards are live. The breakdown is GET /sales/loyalty/discount-performance;
 * the log is GET /sales/loyalty/discount-log, which is backend paginated and
 * sorted — page, sort direction and the source filter all round-trip.
 */
export function DiscountBreakdownAndLog({
    sym, startISO, endISO, outletId = null, locationId = null, isToday = false, orderSource, token = null, onOpenOrder, children,
}: DiscountBreakdownAndLogProps) {
    const isDesktop = useIsDesktop();
    /* Breakdown pills and the Discount Log's own column filter are
       INDEPENDENT selections — picking a source in one must not scope the
       other. Two separate Sets, two separate `source` params. */
    const [selected, setSelected] = useState<Set<DiscountSourceKey>>(() => new Set(ALL_KEYS));
    const [logSelected, setLogSelected] = useState<Set<DiscountSourceKey>>(() => new Set(ALL_KEYS));
    const [logPage, setLogPage] = useState(0);
    const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");

    /* One source selected → let the backend scope the aggregation via `source`.
       "All Sources", or a two-source selection from the Discount-column filter
       (which the single-valued `source` param cannot express), fetches
       everything and narrows client-side. */
    const apiSource: DiscountPerformanceSource | undefined =
        selected.size === 1 ? KEY_TO_API_SOURCE[[...selected][0]] : undefined;
    const logApiSource: DiscountPerformanceSource | undefined =
        logSelected.size === 1 ? KEY_TO_API_SOURCE[[...logSelected][0]] : undefined;

    // Today → no date params (live window); historical → send the range.
    const { data: perf, loading: perfLoading, error: perfError, refetch: perfRetry } = useDiscountPerformance({
        locationId,
        startDate: isToday ? undefined : startISO,
        endDate: isToday ? undefined : endISO,
        source: apiSource,
        orderSource,
        token,
    });

    /* The log is server-paginated, so page / sort / source all go to the API —
       filtering or slicing a page client-side would desync the row count and
       the pager. `page` is 1-based on the endpoint. */
    const { data: logData, loading: logLoading, error: logError, refetch: logRetry } = useDiscountLog({
        locationId,
        startDate: isToday ? undefined : startISO,
        endDate: isToday ? undefined : endISO,
        source: logApiSource,
        orderSource,
        sortBy: LOG_SORT_BY,
        sortOrder: sortDir,
        page: logPage + 1,
        size: LOG_PAGE,
        token,
    });

    /* Live by-source amounts, keyed like the sample breakdown so the pills,
       bars and footer keep working unchanged. Only the amounts come from the
       API — display names stay on DISCOUNT_SOURCES so the pill and its bar
       always read the same, and the copy is owned by the frontend. The
       backend returns its own THIRD_PARTY segment alongside PROMOTIONAL /
       LOYALTY / MANUAL, so this is a straight fold — no client-side split. */
    const breakdown = useMemo(() => {
        const amounts = amountsFromSegments(perf?.segments);
        const segTotal = ALL_KEYS.reduce((a, k) => a + amounts[k], 0);
        // Trust the API total, but fall back to the segment sum if it is absent.
        return { ...amounts, total: perf?.totalDiscount || segTotal };
    }, [perf]);

    useEffect(() => { setLogPage(0); }, [logSelected, startISO, endISO, outletId, sortDir]);

    const setAll = () => setSelected(new Set(ALL_KEYS));
    const setOnly = (k: DiscountSourceKey) => setSelected(new Set([k]));
    // The log's own column filter (SourceFilterControl) owns its draft state and only
    // reaches setLogSelected once, via onApply — see the "true multi-select" note at
    // `logIsPartialMulti` below for why anything but exactly one source narrows client-side.

    const total = breakdown.total;
    const isAll = selected.size === DISCOUNT_SOURCES.length;
    const scopedTotal = ALL_KEYS.filter((k) => selected.has(k)).reduce((a, k) => a + breakdown[k], 0);
    const visibleSources = DISCOUNT_SOURCES.filter((s) => selected.has(s.key));
    const selectionLabel = isAll ? "" : ALL_KEYS.filter((k) => selected.has(k)).map(labelFor).join(", ");
    const logIsAll = logSelected.size === DISCOUNT_SOURCES.length;
    const logSelectionLabel = logIsAll ? "" : ALL_KEYS.filter((k) => logSelected.has(k)).map(labelFor).join(", ");
    /* A true multi-select (2+ of the 4 sources) can't be expressed by the
       single-valued `source` param, so this mode fetches the whole unfiltered
       log once (below) and narrows + paginates it client-side instead. */
    const logIsPartialMulti = !logIsAll && logSelected.size > 1;

    /* Unfiltered log for `logIsPartialMulti`. Deliberately NOT keyed on
       `logSelected` — every subset within this mode reuses the same
       unfiltered fetch; only the client-side filter below reacts to which
       boxes are checked, so toggling a checkbox never refetches. */
    const [multiLogAll, setMultiLogAll] = useState<DiscountLogItem[]>([]);
    const [multiLogLoading, setMultiLogLoading] = useState(false);
    const [multiLogError, setMultiLogError] = useState<string | null>(null);
    const [multiLogReloadKey, setMultiLogReloadKey] = useState(0);
    const multiLogRetry = useCallback(() => setMultiLogReloadKey((k) => k + 1), []);

    useEffect(() => {
        if (!logIsPartialMulti || !locationId) { setMultiLogAll([]); return; }
        let cancelled = false;
        setMultiLogLoading(true);
        setMultiLogError(null);
        (async () => {
            try {
                const base = {
                    locationId,
                    startDate: isToday ? undefined : startISO,
                    endDate: isToday ? undefined : endISO,
                    orderSource,
                    sortBy: LOG_SORT_BY,
                    sortOrder: sortDir,
                    token,
                };
                const probe = await fetchDiscountLog({ ...base, page: 1, size: 1 });
                const total = probe.totalElements || 0;
                const res = total > 0 ? await fetchDiscountLog({ ...base, page: 1, size: total }) : { content: [] as DiscountLogItem[] };
                if (!cancelled) setMultiLogAll(res.content);
            } catch (err: any) {
                if (!cancelled) { setMultiLogError(err?.message || "Failed to load discount log"); setMultiLogAll([]); }
            } finally {
                if (!cancelled) setMultiLogLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [logIsPartialMulti, locationId, startISO, endISO, isToday, orderSource, sortDir, token, multiLogReloadKey]);

    const multiLogFiltered = useMemo(
        () => multiLogAll.filter((r) => logSelected.has(API_SOURCE_TO_KEY[String(r.source).toUpperCase()])),
        [multiLogAll, logSelected]
    );

    const pills = (["all", ...ALL_KEYS] as ("all" | DiscountSourceKey)[]);
    /* Desktop: top-right of the card header. Mobile: trailing the source chips
       — the title wraps to the full width there, which would otherwise push the
       button onto a line of its own between the subtitle and the chips. */
    const exportBtn = !perfLoading && !perfError && total > 0
        ? (
            <ReportSectionExportButton
                        variant={isDesktop ? "desktop" : "mobile"}
                        title="Discount by Source"
                        filename="discount-by-source"
                        /* Refetches on click with the card's own params, so the
                           file carries fresh server data rather than whatever
                           was rendered when the drill-down was opened. */
                        buildExport={async (_format, opts) => {
                            const res = await fetchDiscountPerformance({
                                locationId: opts?.locationId ?? locationId,
                                startDate: isToday ? undefined : startISO,
                                endDate: isToday ? undefined : endISO,
                                source: apiSource,
                                orderSource,
                                token,
                            });
                            const amounts = amountsFromSegments(res.segments);
                            // Sum only the SOURCES BEING EXPORTED (the current source
                            // filter), not `res.totalDiscount` — that field is the
                            // period's grand total across every source regardless of
                            // the filter, so with e.g. only "Manual" selected it would
                            // show the full unfiltered total next to a single $0 row.
                            const exportTotal = visibleSources.reduce((a, s) => a + amounts[s.key], 0);
                            const money = (n: number) => formatMoneyForExport(n);
                            // Symbol lives in the cell (formatMoneyForExport), not the
                            // header — so each row states its own currency, which an
                            // "All outlets" export can span across outlets.
                            const share = (n: number) => (exportTotal > 0 ? `${((n / exportTotal) * 100).toFixed(1)}%` : "—");
                            return {
                                totalRow: true,
                                headers: ["Source", "Discount", "Share"],
                                rows: [
                                    ...visibleSources.map((s) => [s.label, money(amounts[s.key]), share(amounts[s.key])]),
                                    ["Total", money(exportTotal), exportTotal > 0 ? "100.0%" : "—"],
                                ],
                            };
                }}
            />
        ) : null;

    const breakdownCard = (
        <div className="rpt-card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discount &amp; Promotion Performance</div>
                    <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>By source · Loyalty Redemption reconciles with the Loyalty Report</div>
                </div>
                {isDesktop && exportBtn}
            </div>
            {/* Always mounted: each chip refetches, so hiding them behind the
                shimmer would make them flicker away on every click — and an
                empty or failed result would leave no way back to All Sources. */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {pills.map((k) => {
                    const activeTab = k === "all" ? isAll : (selected.size === 1 && selected.has(k as DiscountSourceKey));
                    const label = k === "all" ? (isDesktop ? "All Sources" : "All") : (isDesktop ? labelFor(k as DiscountSourceKey) : DISCOUNT_SOURCES.find((s) => s.key === k)!.mobileLabel);
                    return (
                        <button key={k} type="button" onClick={() => (k === "all" ? setAll() : setOnly(k as DiscountSourceKey))}
                            style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${activeTab ? T.accent : T.cardBorder}`, background: activeTab ? T.accentSoft : T.surface, color: activeTab ? T.accent : T.t2, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
                            {label}
                        </button>
                    );
                })}
                {!isDesktop && exportBtn && <span style={{ marginLeft: "auto", display: "inline-flex" }}>{exportBtn}</span>}
            </div>
            {perfLoading ? (
                <div>
                    {visibleSources.map((s, i) => (
                        <div key={s.key} style={{ padding: "10px 0", borderBottom: i < visibleSources.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
                                <Bone w={130} h={12} />
                                <Bone w={90} h={12} />
                            </div>
                            <Bone w="100%" h={8} r={4} />
                        </div>
                    ))}
                </div>
            ) : perfError ? (
                <SectionError kpiName="discount performance" onRetry={perfRetry} minHeight={160} />
            ) : total <= 0 ? (
                <SectionEmpty kpiName="discounts by source" minHeight={160} />
            ) : (
                <>
                    {visibleSources.map((s, i) => {
                        const val = breakdown[s.key];
                        const share = total > 0 ? (val / total) * 100 : 0;
                        const color = srcColor(s.key);
                        return (
                            <div key={s.key} style={{ padding: "10px 0", borderBottom: i < visibleSources.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5, gap: 8 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>
                                        {s.label}
                                        {s.key === "loyalty" && <span style={{ fontSize: 10, color: T.t3, fontWeight: 400, marginLeft: 6 }}>liability release</span>}
                                    </span>
                                    <span style={{ fontSize: 12, color: T.t2, whiteSpace: "nowrap" }}><strong style={{ color }}>{fmtMoney(val, sym)}</strong> · {share.toFixed(1)}%</span>
                                </div>
                                <div style={{ height: 8, background: T.hover, borderRadius: 4, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, share))}%`, background: `${color}cc`, borderRadius: 4 }} />
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11, color: T.t3 }}>
                        <span>{isAll ? "Total discounts" : "Scoped total"}: <span style={{ color: T.orange, fontWeight: 700 }}>{fmtMoney(scopedTotal, sym)}</span></span>
                    </div>
                </>
            )}
        </div>
    );

    /* `logIsPartialMulti` narrows/paginates `multiLogAll` client-side (the API
       can't filter by multiple sources at once); a lone source selection, or
       "All Sources", stays on the normal server-paginated `logData`. */
    const logTotalElements = logIsPartialMulti ? multiLogFiltered.length : (logData?.totalElements ?? 0);
    const logTotalPages = logIsPartialMulti ? Math.max(1, Math.ceil(multiLogFiltered.length / LOG_PAGE)) : Math.max(1, logData?.totalPages || 1);
    const logSafePage = Math.min(logPage, logTotalPages - 1);
    const rangeFrom = logTotalElements === 0 ? 0 : logSafePage * LOG_PAGE + 1;
    const rangeTo = Math.min((logSafePage + 1) * LOG_PAGE, logTotalElements);
    const logRows = logIsPartialMulti
        ? multiLogFiltered.slice(logSafePage * LOG_PAGE, logSafePage * LOG_PAGE + LOG_PAGE)
        : (logData?.content ?? []);
    const effectiveLogLoading = logIsPartialMulti ? multiLogLoading : logLoading;
    const effectiveLogError = logIsPartialMulti ? multiLogError : logError;
    const effectiveLogRetry = logIsPartialMulti ? multiLogRetry : logRetry;
    const tpl = "0.9fr 1.1fr 0.9fr 1.2fr 1.9fr 0.9fr";
    const cellClip = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 } as const;
    const emptyMsg = logIsAll
        ? "No discounts for the selected period."
        : `No ${logSelectionLabel} discounts for the selected period.`;
    /* Colour and label come off the row's own source, so a badge stays correct
       regardless of the filter. `discount` arrives pre-composed
       ("Loyalty · Redemption"); discountName / source are the fallbacks. */
    const rowColor = (r: DiscountLogItem) => srcColor(API_SOURCE_TO_KEY[String(r.source).toUpperCase()] ?? "");
    const rowBadgeText = (r: DiscountLogItem) => r.discount || r.discountName || String(r.source);
    const LiveBadge = ({ r }: { r: DiscountLogItem }) => (
        <span style={{ display: "inline-block", fontSize: 10, fontWeight: 600, color: rowColor(r), background: `${rowColor(r)}18`, padding: "2px 8px", borderRadius: 20, maxWidth: "100%", ...cellClip }}>{rowBadgeText(r)}</span>
    );
    const liveOrderCell = (r: DiscountLogItem) => (
        <span onClick={onOpenOrder ? () => onOpenOrder({ orderId: r.orderId, orderNo: r.orderNo }) : undefined}
            style={{ fontSize: 11.5, fontWeight: 600, color: onOpenOrder ? T.accent : T.t2, cursor: onOpenOrder ? "pointer" : "default", ...cellClip }} title={r.orderNo}>#{r.orderNo}</span>
    );
    const prevBtn = <button onClick={() => setLogPage((p) => Math.max(0, p - 1))} disabled={logSafePage === 0} style={pagerBtn(logSafePage === 0)}>← Prev</button>;
    const nextBtn = <button onClick={() => setLogPage((p) => Math.min(logTotalPages - 1, p + 1))} disabled={logSafePage >= logTotalPages - 1} style={pagerBtn(logSafePage >= logTotalPages - 1)}>Next →</button>;

    const logCard = (
        <div className="rpt-card" style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discount Log</div>
                    <div style={{ fontSize: 11, color: T.t3 }}>Discounted order details{logIsAll ? "" : ` · ${logSelectionLabel}`}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* Independent from the breakdown pills above — this filter
                        scopes only the log. Multi-select: `source` is
                        single-valued on the API, so any selection other than
                        exactly one source fetches unfiltered and narrows
                        client-side (see `logIsPartialMulti`). */}
                    {!isDesktop && <SourceFilterControl selected={logSelected} onApply={setLogSelected} isDesktop={false} />}
                    {!effectiveLogLoading && !effectiveLogError && logTotalElements > 0 && (
                        <ReportSectionExportButton
                            variant={isDesktop ? "desktop" : "mobile"}
                            title="Discount Log"
                            filename="discount-log"
                            /* Refetches the WHOLE log (size = probed totalElements)
                               so the file carries every row, not just the visible
                               page — and always probes rather than trusting
                               on-screen `logTotalElements`, since that can be the
                               client-filtered count in `logIsPartialMulti` mode. */
                            buildExport={async (_format, opts) => {
                                const exportLoc = opts?.locationId ?? locationId;
                                const base = {
                                    locationId: exportLoc,
                                    startDate: isToday ? undefined : startISO,
                                    endDate: isToday ? undefined : endISO,
                                    source: logApiSource,
                                    orderSource,
                                    sortBy: LOG_SORT_BY,
                                    sortOrder: sortDir,
                                    token,
                                };
                                const probeTotal = (await fetchDiscountLog({ ...base, page: 1, size: 1 })).totalElements;
                                const res = probeTotal > 0 ? await fetchDiscountLog({ ...base, page: 1, size: probeTotal }) : { content: [] as DiscountLogItem[] };
                                // `source` only narrows server-side for a single
                                // selection — a multi-select comes back unfiltered
                                // and needs this same client-side cut.
                                const rows = logIsAll ? res.content : res.content.filter((r) => logSelected.has(API_SOURCE_TO_KEY[String(r.source).toUpperCase()]));
                                // Columns mirror the on-screen table exactly. The
                                // Amount cell carries its own currency symbol
                                // (formatMoneyForExport), not the header — so an
                                // "All outlets" export can span currencies per row.
                                return {
                                    headers: ["Order #", "Date", "Time", "Staff", "Discount", "Amount"],
                                    rows: rows.map((r) => [`#${r.orderNo}`, r.orderDate, fmtTime(r.orderTime), r.staff, rowBadgeText(r), formatMoneyForExport(r.amount)]),
                                };
                            }}
                        />
                    )}
                </div>
            </div>
            {effectiveLogLoading ? (
                <div style={{ marginTop: 12 }}>
                    {Array.from({ length: 5 }, (_, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < 4 ? `1px solid ${T.divider}` : "none" }}>
                            <Bone w={54} h={12} />
                            <Bone w={74} h={12} />
                            <div style={{ flex: 1 }}><Bone w={110} h={12} /></div>
                            <Bone w={96} h={16} r={20} />
                            <Bone w={56} h={12} />
                        </div>
                    ))}
                </div>
            ) : effectiveLogError ? (
                <SectionError kpiName="discount log" onRetry={effectiveLogRetry} minHeight={160} />
            ) : isDesktop ? (
                <div style={{ marginTop: 8 }}>
                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: 640 }}>
                            <div style={{ display: "grid", gridTemplateColumns: tpl, gap: 8, padding: "6px 0", borderBottom: `1px solid ${T.divider}`, marginBottom: 4 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Order #</div>
                                <div onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, cursor: "pointer", userSelect: "none" }}>
                                    Date <span style={{ marginLeft: 2 }}>{sortDir === "desc" ? "▾" : "▴"}</span>
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Time</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Staff</div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 4 }}>
                                    Discount <SourceFilterControl selected={logSelected} onApply={setLogSelected} isDesktop />
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "right" }}>Amount</div>
                            </div>
                            {logRows.map((r, i) => (
                                <div key={`${r.orderId}-${i}`} style={{ display: "grid", gridTemplateColumns: tpl, gap: 8, padding: "8px 0", alignItems: "center", borderBottom: i < logRows.length - 1 ? `1px solid ${T.divider}` : "none" }}>
                                    <div style={cellClip}>{liveOrderCell(r)}</div>
                                    <div style={{ fontSize: 11.5, color: T.t2, fontFamily: T.font, whiteSpace: "nowrap" }}>{r.orderDate}</div>
                                    <div style={{ fontSize: 11, color: T.t3, whiteSpace: "nowrap" }}>{fmtTime(r.orderTime)}</div>
                                    <div title={r.staff} style={{ fontSize: 11.5, fontWeight: 600, color: r.staff === "System" ? T.t2 : T.accent, ...cellClip }}>{r.staff}</div>
                                    <div style={cellClip}><LiveBadge r={r} /></div>
                                    <div style={{ fontSize: 11.5, fontWeight: 600, color: T.orange, textAlign: "right", whiteSpace: "nowrap" }}>{fmtMoney(r.amount, sym)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {logTotalPages > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>{rangeFrom}–{rangeTo} of {logTotalElements}</span>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                {prevBtn}
                                <span style={{ fontSize: 11, color: T.t2, padding: "4px 6px" }}>{logSafePage + 1} / {logTotalPages}</span>
                                {nextBtn}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                    {logRows.map((r, i) => (
                        <div key={`${r.orderId}-${i}`} style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
                                {liveOrderCell(r)}
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.orange }}>{fmtMoney(r.amount, sym)}</span>
                            </div>
                            <div style={{ marginBottom: 6 }}><LiveBadge r={r} /></div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.t3 }}>
                                <span>{r.staff}</span>
                                <span>{r.orderDate} · {fmtTime(r.orderTime)}</span>
                            </div>
                        </div>
                    ))}
                    {logTotalPages > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                            {prevBtn}
                            <span style={{ fontSize: 10, color: T.t3 }}>{logSafePage + 1} / {logTotalPages}</span>
                            {nextBtn}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div>
            {breakdownCard}
            {children}
            {logCard}
        </div>
    );
}
